import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, SlidersHorizontal, Check, Plus, Minus, AlertTriangle } from 'lucide-react';
import { Button } from './SubscriptionAppButton';
import { Checkbox } from './Checkbox';
// Types
export interface Product {
  id: string;
  name: string;
  subtitle: string;
  price: number;
  stock: number;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
  color: string; // for the bottle placeholder
}
export interface ProductSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[], quantities?: Record<string, number>) => void;
  mode: 'custom' | 'curated';
  packMin?: number; // Required for curated mode validation
  packMax?: number; // Required for curated mode validation
}
// Mock Data
const MOCK_PRODUCTS: Product[] = [{
  id: '1',
  name: '2022 Reserve Shiraz',
  subtitle: '750ml · Red Wine',
  price: 48.0,
  stock: 156,
  status: 'In Stock',
  color: '#FECACA' // Red-200
}, {
  id: '2',
  name: '2021 Chardonnay Reserve',
  subtitle: '750ml · White Wine',
  price: 42.0,
  stock: 8,
  status: 'Low Stock',
  color: '#FEF3C7' // Amber-100
}, {
  id: '3',
  name: 'Cabernet Sauvignon 2019',
  subtitle: '750ml · Red Wine',
  price: 58.0,
  stock: 89,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '4',
  name: 'Sauvignon Blanc 2023',
  subtitle: '750ml · White Wine',
  price: 32.0,
  stock: 243,
  status: 'In Stock',
  color: '#FEF3C7'
}, {
  id: '5',
  name: 'Rosé 2023',
  subtitle: '750ml · Rosé',
  price: 28.0,
  stock: 178,
  status: 'In Stock',
  color: '#FCE7F3' // Pink-100
}, {
  id: '6',
  name: 'Merlot 2021',
  subtitle: '750ml · Red Wine',
  price: 38.0,
  stock: 67,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '7',
  name: 'Riesling 2023',
  subtitle: '750ml · White Wine',
  price: 30.0,
  stock: 134,
  status: 'In Stock',
  color: '#FEF3C7'
}, {
  id: '8',
  name: 'Pinot Noir Estate 2020',
  subtitle: '750ml · Red Wine',
  price: 55.0,
  stock: 0,
  status: 'Out of Stock',
  color: '#FECACA'
}, {
  id: '9',
  name: 'Tempranillo 2020',
  subtitle: '750ml · Red Wine',
  price: 44.0,
  stock: 23,
  status: 'In Stock',
  color: '#FECACA'
}, {
  id: '10',
  name: 'GSM Blend 2021',
  subtitle: '750ml · Red Wine',
  price: 36.0,
  stock: 91,
  status: 'In Stock',
  color: '#FECACA'
}];
export function ProductSourceModal({
  isOpen,
  onClose,
  onConfirm,
  mode,
  packMin = 0,
  packMax = 0
}: ProductSourceModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  // Initialize default selections based on mode
  useEffect(() => {
    if (isOpen) {
      if (mode === 'curated') {
        // Default curated selection: first 6 items
        // Shiraz: 2, Chardonnay: 2, Cabernet: 2, Sauv Blanc: 1, Rosé: 1, Merlot: 2 => Total 10
        const initialSelected = new Set(['1', '2', '3', '4', '5', '6']);
        const initialQuantities = {
          '1': 2,
          '2': 2,
          '3': 2,
          '4': 1,
          '5': 1,
          '6': 2
        };
        setSelectedIds(initialSelected);
        setQuantities(initialQuantities);
      } else {
        // Default custom selection: everything except Out of Stock (id 8) and one other (id 9)
        const initialSelected = new Set(['1', '2', '3', '4', '5', '6', '7', '10']);
        setSelectedIds(initialSelected);
        setQuantities({});
      }
    }
  }, [isOpen, mode]);
  // Filter products
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return MOCK_PRODUCTS;
    return MOCK_PRODUCTS.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  // Calculations
  const totalBottlesSelected = useMemo(() => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  }, [quantities]);
  const estimatedTotal = useMemo(() => {
    return Array.from(selectedIds).reduce((sum, id) => {
      const product = MOCK_PRODUCTS.find(p => p.id === id);
      const qty = quantities[id] || (mode === 'curated' ? 0 : 1);
      if (mode === 'curated') {
        return sum + (product?.price || 0) * qty;
      }
      return sum;
    }, 0);
  }, [selectedIds, quantities, mode]);
  // Handlers
  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
      if (mode === 'curated') {
        const newQuantities = {
          ...quantities
        };
        delete newQuantities[id];
        setQuantities(newQuantities);
      }
    } else {
      newSet.add(id);
      if (mode === 'curated') {
        setQuantities(prev => ({
          ...prev,
          [id]: 1
        }));
      }
    }
    setSelectedIds(newSet);
  };
  const updateQuantity = (id: string, delta: number) => {
    const currentQty = quantities[id] || 0;
    const newQty = Math.max(1, currentQty + delta);
    setQuantities(prev => ({
      ...prev,
      [id]: newQty
    }));
  };
  // Select All logic
  const allFilteredSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));
  const someFilteredSelected = filteredProducts.some(p => selectedIds.has(p.id)) && !allFilteredSelected;
  const toggleSelectAll = () => {
    const newSet = new Set(selectedIds);
    const newQuantities = {
      ...quantities
    };
    if (allFilteredSelected) {
      // Deselect all filtered products
      filteredProducts.forEach(p => {
        newSet.delete(p.id);
        if (mode === 'curated') {
          delete newQuantities[p.id];
        }
      });
    } else {
      // Select all filtered products
      filteredProducts.forEach(p => {
        if (!newSet.has(p.id)) {
          newSet.add(p.id);
          if (mode === 'curated') {
            newQuantities[p.id] = 1;
          }
        }
      });
    }
    setSelectedIds(newSet);
    if (mode === 'curated') {
      setQuantities(newQuantities);
    }
  };
  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds), mode === 'curated' ? quantities : undefined);
    onClose();
  };
  if (!isOpen) return null;
  const isOverLimit = mode === 'curated' && totalBottlesSelected > packMax;
  const isUnderLimit = mode === 'curated' && totalBottlesSelected < packMin;
  const isConfirmDisabled = mode === 'curated' && isUnderLimit;
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-2320">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-2321" />


      {/* Modal Container */}
      <div className="relative w-full max-w-[900px] max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-2322">
        {/* Header */}
        <div className="flex-none px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between bg-white" data-id="element-2323">
          <div data-id="element-2324">
            <h2 className="text-[16px] font-bold text-[#0F172A]" data-id="element-2325">
              Select Products
            </h2>
            <div className="mt-1 flex items-center gap-2" data-id="element-2326">
              {mode === 'curated' ? <div className={`flex items-center gap-2 text-[13px] font-medium ${isOverLimit ? 'text-[#EF4444]' : isUnderLimit ? 'text-[#F59E0B]' : 'text-[#10B981]'}`} data-id="element-2327">

                  {isOverLimit || isUnderLimit ? <AlertTriangle className="w-4 h-4" data-id="element-2328" /> : <Check className="w-4 h-4" data-id="element-2329" />}
                  {totalBottlesSelected} of {packMax} bottles assigned
                  {isOverLimit && <span className="text-[#EF4444] ml-1" data-id="element-2330">(Over limit)</span>}
                  {isUnderLimit && <span className="text-[#F59E0B] ml-1" data-id="element-2331">
                      (Minimum {packMin})
                    </span>}
                </div> : <p className="text-[14px] text-[#64748B]" data-id="element-2332">
                  {selectedIds.size} of {MOCK_PRODUCTS.length} products selected
                </p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-2333">

            <X className="w-5 h-5" data-id="element-2334" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-[#F8FAFC]" data-id="element-2335">
          <div className="p-6 space-y-6" data-id="element-2336">
            {/* Search & Filter */}
            <div className="flex gap-3" data-id="element-2337">
              <div className="relative flex-1" data-id="element-2338">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-2339" />
                <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-10 pl-10 pr-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-2340" />

              </div>
              <Button variant="outline" iconLeft={<SlidersHorizontal className="w-4 h-4" data-id="element-2342" />} data-id="element-2341">

                Filter
              </Button>
            </div>

            {/* Product Table */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden shadow-sm" data-id="element-2343">
              <table className="w-full" data-id="element-2344">
                <thead data-id="element-2345">
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-2346">
                    <th className="w-12 py-3 pl-4" data-id="element-2347">
                      {mode === 'custom' && <Checkbox checked={allFilteredSelected} indeterminate={someFilteredSelected} onChange={toggleSelectAll} data-id="element-2348" />}
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-2349">
                      Product
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-2350">
                      Price
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-2351">
                      Stock
                    </th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-2352">
                      Status
                    </th>
                    {mode === 'curated' && <th className="text-right py-3 px-4 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-2353">
                        Quantity
                      </th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-2354">
                  {filteredProducts.map(product => {
                  const isSelected = selectedIds.has(product.id);
                  const quantity = quantities[product.id] || 0;
                  const lineTotal = quantity * product.price;
                  return <tr key={product.id} className={`transition-colors ${isSelected && mode === 'curated' ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} data-id="element-2355">

                        <td className="py-3 pl-4 align-middle" data-id="element-2356">
                          <Checkbox checked={isSelected} onChange={() => toggleSelection(product.id)} data-id="element-2357" />

                        </td>
                        <td className="py-3 px-4 align-middle" data-id="element-2358">
                          <div className="flex items-center gap-3" data-id="element-2359">
                            <div className="w-8 h-10 rounded flex-shrink-0 flex items-center justify-center" style={{
                          backgroundColor: product.color
                        }} data-id="element-2360">

                              <div className="w-3 h-6 border-2 border-white/40 rounded-full opacity-50" data-id="element-2361" />
                            </div>
                            <div data-id="element-2362">
                              <div className="text-[14px] font-bold text-[#0F172A]" data-id="element-2363">
                                {product.name}
                              </div>
                              <div className="text-[12px] text-[#64748B]" data-id="element-2364">
                                {product.subtitle}
                              </div>
                              {mode === 'curated' && isSelected && <div className="text-[12px] text-[#0EA5E9] font-medium mt-0.5" data-id="element-2365">
                                  {quantity} × ${product.price.toFixed(2)} = $
                                  {lineTotal.toFixed(2)}
                                </div>}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[14px] font-medium text-[#0F172A] align-middle" data-id="element-2366">
                          ${product.price.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-[14px] text-[#64748B] align-middle" data-id="element-2367">
                          {product.stock}
                        </td>
                        <td className="py-3 px-4 align-middle" data-id="element-2368">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${product.status === 'In Stock' ? 'bg-[#D1FAE5] text-[#059669]' : product.status === 'Low Stock' ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#FEE2E2] text-[#DC2626]'}`} data-id="element-2369">

                            {product.status}
                          </span>
                        </td>
                        {mode === 'curated' && <td className="py-3 px-4 align-middle text-right" data-id="element-2370">
                            {isSelected && <div className="flex items-center justify-end gap-2" data-id="element-2371">
                                <button onClick={() => updateQuantity(product.id, -1)} className="w-8 h-8 flex items-center justify-center rounded border border-[#CBD5E1] bg-white text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-2372">

                                  <Minus className="w-3 h-3" data-id="element-2373" />
                                </button>
                                <input type="number" value={quantity} onChange={e => {
                          const val = parseInt(e.target.value) || 0;
                          setQuantities(prev => ({
                            ...prev,
                            [product.id]: Math.max(1, val)
                          }));
                        }} className="w-12 h-8 text-center border border-[#CBD5E1] rounded text-[13px] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" data-id="element-2374" />

                                <button onClick={() => updateQuantity(product.id, 1)} className="w-8 h-8 flex items-center justify-center rounded border border-[#CBD5E1] bg-white text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-2375">

                                  <Plus className="w-3 h-3" data-id="element-2376" />
                                </button>
                              </div>}
                          </td>}
                      </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-none px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-between" data-id="element-2377">
          <div className="text-[13px] font-medium text-[#64748B]" data-id="element-2378">
            {mode === 'curated' ? <span data-id="element-2379">
                {selectedIds.size} products selected · {totalBottlesSelected}{' '}
                bottles · Est. ${estimatedTotal.toFixed(2)}
              </span> : <span data-id="element-2380">{selectedIds.size} products selected</span>}
          </div>
          <div className="flex gap-3" data-id="element-2381">
            <Button variant="outline" onClick={onClose} data-id="element-2382">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled} data-id="element-2383">

              Confirm Selection
            </Button>
          </div>
        </div>
      </div>
    </div>;
}